import { tg } from "./telegram";

export type ThemePref = "auto" | "light" | "dark";

const STORAGE_KEY = "nipagesh_theme";

export function loadThemePref(): ThemePref {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "auto") return v;
  } catch {
    // localStorage blocked — fall back to auto
  }
  return "auto";
}

export function saveThemePref(pref: ThemePref) {
  try {
    localStorage.setItem(STORAGE_KEY, pref);
  } catch {
    // ignore
  }
}

/** Computes the effective scheme from a user preference + current Telegram state. */
function resolveScheme(pref: ThemePref): "light" | "dark" {
  if (pref === "light" || pref === "dark") return pref;
  const tgScheme = tg()?.colorScheme;
  if (tgScheme === "dark") return "dark";
  if (tgScheme === "light") return "light";
  // Outside Telegram — fall back to system preference
  if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
    return "dark";
  }
  return "light";
}

export function applyTheme(pref: ThemePref) {
  const scheme = resolveScheme(pref);
  const html = document.documentElement;
  html.classList.toggle("theme-dark", scheme === "dark");
  html.style.colorScheme = scheme;
}

/** Call once on app init. Wires up Telegram themeChanged listener. */
export function initTheme() {
  const pref = loadThemePref();
  applyTheme(pref);

  // Re-apply when Telegram changes its theme (only matters if pref === 'auto')
  const app = tg() as (ReturnType<typeof tg> & {
    onEvent?: (e: string, cb: () => void) => void;
  }) | null;
  app?.onEvent?.("themeChanged", () => {
    const p = loadThemePref();
    if (p === "auto") applyTheme(p);
  });
}
